import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Block, Typography } from '@aloft/toolkit-sui';

import type React from 'react';

import { selectHasArvOption } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectCompTypeVisibility } from '../../../redux/features/selectors/propertyDataSelectors/compTypeSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { AdjustmentField } from '../fields/AdjustmentField.tsx';
import { EditableDateField } from '../fields/EditableDateField.tsx';
import { EditableComboboxField } from '../fields/EditableComboboxField.tsx';
import { EditableEnumField } from '../fields/EditableEnumField.tsx';
import { EditableField } from '../fields/EditableField.tsx';
import { EditableUnitRateField } from '../fields/EditableUnitRateField.tsx';
import { LabelField } from '../fields/LabelField.tsx';
import { ReadOnlyDifferenceField } from '../fields/ReadOnlyDifferenceField.tsx';
import { ReadOnlyField } from '../fields/ReadOnlyField.tsx';
import { ReadOnlyUnitRateField } from '../fields/ReadOnlyUnitRateField.tsx';
import { SimpleEditableField } from '../fields/SimpleEditableField.tsx';
import { AgeWithYearBuiltField } from '../fields/customFields/AgeWithYearBuiltField.tsx';
import { AgeWithYearBuiltReadOnlyField } from '../fields/customFields/AgeWithYearBuiltReadOnlyField.tsx';
import { BasementAreaField } from '../fields/customFields/BasementAreaField.tsx';
import { BasementTypeField } from '../fields/customFields/BasementTypeField.tsx';
import {
  ContractDateField,
  LeaseDateField,
  SoldDateField,
} from '../fields/customFields/ConditionalDateField.tsx';
import { SubjectTotalCostToCureField } from '../fields/customFields/SubjectTotalCostToCureField.tsx';
import { readOnlyFieldTypographyProps } from '../fields/styles.ts';
import type { PropertyGridFieldProps } from '../fields/types.ts';
import type { PropertyGridField, PropertyGridSectionProps } from '../types.ts';

type PropertyGridRowProps = Pick<
  PropertyGridSectionProps,
  | 'columnIndex'
  | 'fieldTypes'
  | 'gridType'
  | 'hasSeparateArvValue'
  | 'control'
  | 'getValues'
  | 'setValue'
> & {
  field: PropertyGridField;
  sectionIsNone?: boolean;
  arvSectionIsNone?: boolean;
};

export const PropertyGridRow = ({
  columnIndex,
  fieldTypes,
  gridType,
  field,
  sectionIsNone = false,
  hasSeparateArvValue = false,
  arvSectionIsNone = false,
  control,
  getValues,
  setValue,
}: PropertyGridRowProps) => {
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);
  const { showAivComps, showArvComps } = useAppSelector(selectCompTypeVisibility);

  const { fieldName, required } = field;

  const columnType = getValues(`columns.${columnIndex}.type`);
  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
  const requiredForCell =
    required && !(isSubject && fieldName.startsWith('editableData.transaction.'));

  const arvIsEnabled = hasArvOption && showArvComps;
  const showSeparateArvField =
    columnType === 'property' && isSubject && arvIsEnabled && hasSeparateArvValue;

  return (
    <>
      {fieldTypes.map((fieldType) => {
        let FieldComponent: React.ComponentType<PropertyGridFieldProps> | undefined;
        let isArv = false;
        let colSpan = 1;

        // Special case for age fields - use custom component with year built
        const isAgeField = fieldName === 'editableData.improvements.age';
        // Special case for date fields - use date picker component
        const isDateField = [
          'editableData.transaction.contractDate',
          'editableData.transaction.leaseDate',
          'editableData.transaction.saleDate',
          'editableData.transaction.expirationDate',
          'editableData.transaction.withdrawalDate',
        ].includes(fieldName);

        const isSoldDateField = fieldName === 'editableData.transaction.saleDate';
        const isContractDateField = fieldName === 'editableData.transaction.contractDate';
        const isLeaseDateField = fieldName === 'editableData.transaction.leaseDate';
        const isPropertyTypeField = fieldName === 'editableData.improvements.propertyType';
        // Special case for basement Type/Total Area/Finished Area - Total Area and Finished Area
        // are derived from one another depending on Type, so they need sibling-aware
        // enable/disable + sync logic that generic property grid fields don't support.
        const isBasementTypeField = fieldName === 'editableData.improvements.basement.type';
        const isBasementAreaField =
          fieldName === 'editableData.improvements.basement.totalArea' ||
          fieldName === 'editableData.improvements.basement.finishedArea';

        const isAdjustmentsGrid = gridType === 'AdjustmentsGrid';
        const isOtherAdjustmentsField = isAdjustmentsGrid && field.manualAdjustmentKey === 'other';
        const isTotalCostToCureAdjustmentField =
          isAdjustmentsGrid && field.manualAdjustmentKey === 'totalCostToCure';

        switch (fieldType) {
          case 'dataSourceValue':
            FieldComponent = ReadOnlyField;
            break;
          case 'editableArvValue':
            if (!showSeparateArvField) break;

            if (activeTab !== 'arv' && isSubject && (isAgeField || isPropertyTypeField)) {
              break;
            }
            isArv = true;
          // eslint-disable-next-line no-fallthrough
          case 'editableValue': {
            const shouldHideSubjectEditableValue =
              isSubject && !showAivComps && !isArv && hasSeparateArvValue;
            if (shouldHideSubjectEditableValue) break;
            if (isSubject && !isArv && arvIsEnabled && !hasSeparateArvValue) {
              colSpan = activeTab === 'all' ? 2 : 1;
            }

            if ((sectionIsNone && !isArv) || (arvSectionIsNone && isArv)) {
              return (
                <Block
                  key={`${columnIndex}-${field.manualAdjustmentKey ?? fieldName}-${fieldType}-none`}
                  gridColumn={`span ${colSpan}`}
                >
                  <Typography {...readOnlyFieldTypographyProps}>None</Typography>
                </Block>
              );
            }

            if (isSubject && hasArvOption && (isAgeField || isPropertyTypeField)) {
              colSpan = activeTab === 'all' ? 2 : 1;
            }

            if (isAgeField) FieldComponent = AgeWithYearBuiltField;
            else if (isSoldDateField) FieldComponent = SoldDateField;
            else if (isContractDateField) FieldComponent = ContractDateField;
            else if (isLeaseDateField) FieldComponent = LeaseDateField;
            else if (isDateField) FieldComponent = EditableDateField;
            else if (isBasementTypeField) FieldComponent = BasementTypeField;
            else if (isBasementAreaField) FieldComponent = BasementAreaField;
            else if ((field.allowCustom || field.typeahead) && field.options)
              FieldComponent = EditableComboboxField;
            else if (field.options) FieldComponent = EditableEnumField;
            else FieldComponent = EditableField;
            break;
          }
          case 'readOnlyArvValue':
            if (
              isTotalCostToCureAdjustmentField &&
              isSubject &&
              fieldTypes.includes('editableUnitRate')
            ) {
              return (
                <Block
                  key={`${columnIndex}-${field.manualAdjustmentKey}-${fieldType}-blank`}
                  gridColumn={`span ${colSpan}`}
                />
              );
            }
            if (!isAdjustmentsGrid && !showSeparateArvField) break;
            if (isAdjustmentsGrid && isSubject && !hasSeparateArvValue && activeTab === 'all') {
              break;
            }
            if (isTotalCostToCureAdjustmentField) {
              if (fieldTypes.includes('readOnlyValue')) break;
            } else if (hasSeparateArvValue) {
              isArv = true;
            }
          // eslint-disable-next-line no-fallthrough
          case 'readOnlyValue':
            if (
              isTotalCostToCureAdjustmentField &&
              isSubject &&
              fieldTypes.includes('editableUnitRate')
            ) {
              return (
                <Block
                  key={`${columnIndex}-${field.manualAdjustmentKey}-${fieldType}-blank`}
                  gridColumn={`span ${colSpan}`}
                />
              );
            }
            if (isSubject && !isArv && arvIsEnabled && !hasSeparateArvValue) {
              colSpan = activeTab === 'all' ? 2 : 1;
            }

            if (isTotalCostToCureAdjustmentField) {
              colSpan = isSubject && hasArvOption && activeTab === 'all' ? 2 : 1;
            }

            if (isOtherAdjustmentsField) {
              FieldComponent = isSubject ? SimpleEditableField : ReadOnlyField;
            } else if (isAgeField) FieldComponent = AgeWithYearBuiltReadOnlyField;
            else if (isTotalCostToCureAdjustmentField) {
              FieldComponent = isSubject ? SubjectTotalCostToCureField : SimpleEditableField;
            } else if (
              isAdjustmentsGrid &&
              (field.manualAdjustmentKey === 'quality' || field.manualAdjustmentKey === 'condition')
            ) {
              FieldComponent = EditableEnumField;
            } else FieldComponent = ReadOnlyField;
            break;
          case 'adjustment':
            FieldComponent = AdjustmentField;
            break;
          case 'editableUnitRate':
            FieldComponent = isTotalCostToCureAdjustmentField
              ? SubjectTotalCostToCureField
              : EditableUnitRateField;
            break;
          case 'readOnlyUnitRate':
            FieldComponent = ReadOnlyUnitRateField;
            break;
          case 'readOnlyDifference':
            if (
              gridType === 'SensitivityGrid' &&
              hasArvOption &&
              activeTab === 'all' &&
              isSubject
            ) {
              colSpan = 2;
            }
            FieldComponent = ReadOnlyDifferenceField;
            break;
          default:
            FieldComponent = LabelField;
            break;
        }

        if (!FieldComponent) return null;

        return (
          <FieldComponent
            key={`${columnIndex}-${field.manualAdjustmentKey ?? fieldName}-${fieldType}-field`}
            columnIndex={columnIndex}
            field={field}
            isArv={isArv}
            colSpan={colSpan}
            control={control}
            getValues={getValues}
            setValue={setValue}
            required={requiredForCell}
            gridType={gridType}
          />
        );
      })}
    </>
  );
};
